import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { proposeTasks, assigneeLabel, type ProposedTask } from "../lib/automation";
import type {
  Automation, AutomationTriggerType, AutomationCondition, ConditionOp, Assignee, TaskPriority, Task, Workspace, TaskType,
} from "../lib/types";
import * as work from "../lib/workClient";

const TRIGGERS: { type: AutomationTriggerType; label: string; describe: (p: Record<string, string | number>) => string; param: null | { key: string; label: string; kind: "number" | "band" } }[] = [
  { type: "health_below", label: "Health drops below", describe: (p) => `Account health falls under ${p.threshold ?? 45}`, param: { key: "threshold", label: "Health score", kind: "number" } },
  { type: "renewal_within", label: "Renewal approaching", describe: (p) => `Renewal date is within ${p.days ?? 90} days`, param: { key: "days", label: "Days out", kind: "number" } },
  { type: "health_band_is", label: "Health band is", describe: (p) => `Account is in the ${p.band ?? "risk"} band`, param: { key: "band", label: "Band", kind: "band" } },
  { type: "signal_created", label: "Signal is raised", describe: (p) => `A ${p.band ? p.band + " " : ""}signal is open`, param: { key: "band", label: "Signal band", kind: "band" } },
];
const triggerMeta = (t: AutomationTriggerType) => TRIGGERS.find((x) => x.type === t)!;

const OPS: { op: ConditionOp; label: string }[] = [
  { op: "lt", label: "<" }, { op: "lte", label: "≤" }, { op: "gt", label: ">" }, { op: "gte", label: "≥" },
  { op: "eq", label: "is" }, { op: "neq", label: "is not" }, { op: "contains", label: "contains" },
];
const FIELDS = ["health", "arr", "region", "lifecycleStage", "segment", "expansionPotential", "seatsActive", "industry"];
const encodeAssignee = (a: Assignee): string => (a.kind === "role" ? `role:${a.role}` : `user:${a.userId}`);
const parseAssignee = (v: string): Assignee => { const [k, id] = v.split(":"); return k === "role" ? { kind: "role", role: id } : { kind: "user", userId: id }; };

export default function Automations() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [rules, setRules] = useState<Automation[]>(ws.automations);
  const [tasks, setTasks] = useState<Task[]>(ws.tasks);
  const [selectedId, setSelectedId] = useState<string>(ws.automations[0]?.id ?? "");
  const [toast, setToast] = useState<string>("");
  const [taskTypes, setTaskTypes] = useState<TaskType[]>(ws.taskTypes);

  useEffect(() => {
    work.loadAutomations(ws).then((r) => { setMode(r.mode); setRules(r.automations); if (!r.automations.some((a) => a.id === selectedId)) setSelectedId(r.automations[0]?.id ?? ""); });
    work.loadTasks(ws).then((r) => setTasks(r.tasks));
    work.loadTaskTypes(ws).then((r) => setTaskTypes(r.types));

  }, [ws]);

  // a workspace snapshot carrying the current (possibly edited) rules, so the
  // preview reflects what's on screen — exactly what the server engine would do.
  const previewWs: Workspace = useMemo(() => ({ ...ws, automations: rules, tasks }), [ws, rules, tasks]);
  const selected = rules.find((r) => r.id === selectedId) ?? null;

  const enabledCount = rules.filter((r) => r.enabled).length;
  const createdTotal = rules.reduce((s, r) => s + r.createdTaskCount, 0);
  const pendingAll = useMemo(() => proposeTasks(previewWs, tasks).length, [previewWs, tasks]);

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  const patchRule = async (id: string, patch: Partial<Automation>) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    await work.updateAutomation(mode, ws, id, patch);
  };
  const toggle = (r: Automation) => patchRule(r.id, { enabled: !r.enabled });
  const remove = async (r: Automation) => {
    setRules((prev) => prev.filter((x) => x.id !== r.id));
    if (selectedId === r.id) setSelectedId("");
    await work.deleteAutomation(mode, ws, r.id);
  };
  const create = async () => {
    const { ent } = await work.loadEntitlements(ws);
    if (ent.automationsUsed >= ent.automationLimit) { flash(`You've used all ${ent.automationLimit} automations on your plan — add a slot in Settings → Usage.`); return; }
    const a = await work.createAutomation(mode, ws, {
      name: "New automation", enabled: false,
      trigger: { type: "health_below", params: { threshold: 45 } }, conditions: [],
      actions: [{ type: "create_task", title: "Follow up on {account}", assignee: { kind: "role", role: "csm" }, priority: "medium", dueInDays: 3 }],
    });
    setRules((prev) => [...prev, a]);
    setSelectedId(a.id);
  };
  const run = async (onlyId?: string) => {
    const created = await work.runAutomations(mode, ws, tasks, onlyId);
    setTasks((prev) => [...prev, ...created]);
    if (created.length) {
      setRules((prev) => prev.map((r) => {
        const n = created.filter((c) => c.automationId === r.id).length;
        return n ? { ...r, createdTaskCount: r.createdTaskCount + n, lastRunAt: new Date().toISOString() } : r;
      }));
    }
    flash(created.length ? `Created ${created.length} task${created.length > 1 ? "s" : ""}` : "No new tasks — everything already assigned");
  };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Automations</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Rules that do the routing</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            Watch for a condition across the portfolio, then create and assign the task —
            to a person, or to a role that lands on whoever owns it for that account.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <ModePill mode={mode} />
          <button className="btn btn--sm" onClick={() => run()} title="Run all enabled rules now">
            <Icon name="refresh" size={14} /> Run all
          </button>
          <button className="btn btn--primary btn--sm" onClick={create}><Icon name="plus" size={15} /> New rule</button>
        </div>
      </div>

      <div className="stats">
        <Stat icon="bolt" label="Enabled rules" value={`${enabledCount}`} foot={`of ${rules.length} total`} />
        <Stat icon="check" label="Tasks created" value={`${createdTotal}`} foot="By automations so far" />
        <Stat icon="target" label="Pending matches" value={`${pendingAll}`} foot="Would be created on a run now" color={pendingAll ? "var(--corona)" : undefined} />
      </div>

      {toast && <div className="toast"><Icon name="check" size={14} /> {toast}</div>}

      <div className="auto-layout">
        <div style={{ display: "grid", gap: 12 }}>
          {rules.length === 0 ? <Empty>No automations yet. Create your first rule.</Empty> : rules.map((r) => {
            const active = r.id === selectedId;
            const would = proposeTasks({ ...previewWs, automations: [r] }, tasks, r.id).length;
            return (
              <div key={r.id} className="card" onClick={() => setSelectedId(r.id)}
                style={{ cursor: "pointer", borderColor: active ? "var(--corona)" : undefined, boxShadow: active ? "0 0 0 1px var(--corona), var(--shadow)" : undefined }}>
                <div className="card__bd">
                  <div className="row between" style={{ alignItems: "flex-start" }}>
                    <div className="row" style={{ gap: 11 }}>
                      <span className="n-ic" style={{ width: 34, height: 34, borderRadius: 9, display: "grid", placeItems: "center", background: "var(--surface-2)", border: "1px solid var(--line)" }}>
                        <Icon name="bolt" size={16} className="faint" />
                      </span>
                      <div>
                        <b style={{ fontSize: 14.5 }}>{r.name}</b>
                        <small className="faint" style={{ display: "block", fontSize: 12.5 }}>{triggerMeta(r.trigger.type).describe(r.trigger.params ?? {})}</small>
                      </div>
                    </div>
                    <button className="toggle" role="switch" aria-checked={r.enabled} onClick={(e) => { e.stopPropagation(); toggle(r); }}>
                      <span className={`toggle__track ${r.enabled ? "on" : ""}`}><span className="toggle__knob" /></span>
                    </button>
                  </div>
                  <div className="row between" style={{ marginTop: 12 }}>
                    <span className="faint" style={{ fontSize: 12.5 }}>
                      <b style={{ color: "var(--ink)" }}>{r.createdTaskCount}</b> created
                      {would > 0 && <> · <span style={{ color: "var(--corona)" }}>{would} pending</span></>}
                    </span>
                    <span className="tag">{r.actions.length} action{r.actions.length > 1 ? "s" : ""}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          {selected ? (
            <Editor key={selected.id} ws={ws} taskTypes={taskTypes} rule={selected} preview={proposeTasks({ ...previewWs, automations: [selected] }, tasks, selected.id)}
              onChange={(patch) => patchRule(selected.id, patch)} onRun={() => run(selected.id)} onDelete={() => remove(selected)} />
          ) : (
            <div className="card"><div className="card__bd"><Empty>Select a rule to edit it, or create a new one.</Empty></div></div>
          )}
        </div>
      </div>
    </div>
  );
}

function Editor({ ws, taskTypes, rule, preview, onChange, onRun, onDelete }: {
  ws: Workspace; taskTypes: TaskType[]; rule: Automation; preview: ProposedTask[];
  onChange: (patch: Partial<Automation>) => void; onRun: () => void; onDelete: () => void;
}) {
  const meta = triggerMeta(rule.trigger.type);
  const action = rule.actions[0];
  const chosenType = taskTypes.find((t) => t.id === action.taskTypeId);
  const setTrigger = (type: AutomationTriggerType) => {
    const defaults: Record<AutomationTriggerType, Record<string, string | number>> = {
      health_below: { threshold: 45 }, renewal_within: { days: 90 }, health_band_is: { band: "risk" }, signal_created: { band: "risk" },
    };
    onChange({ trigger: { type, params: defaults[type] } });
  };
  const setParam = (key: string, value: string | number) => onChange({ trigger: { ...rule.trigger, params: { ...rule.trigger.params, [key]: value } } });
  const setAction = (patch: Partial<Automation["actions"][number]>) => onChange({ actions: [{ ...action, ...patch }] });
  const setConditions = (conditions: AutomationCondition[]) => onChange({ conditions });

  return (
    <div className="card">
      <div className="card__hd">
        <input className="inp inp--title" value={rule.name} onChange={(e) => onChange({ name: e.target.value })} />
        <button className="icon-btn" title="Delete rule" onClick={onDelete}><Icon name="dot" size={16} /></button>
      </div>
      <div className="card__bd" style={{ display: "grid", gap: 18 }}>
        <textarea className="inp" rows={2} placeholder="What does this rule do?" value={rule.description ?? ""} onChange={(e) => onChange({ description: e.target.value })} />

        {/* WHEN */}
        <section className="rule-sec">
          <div className="rule-sec__hd"><span className="rule-sec__k">When</span> a trigger fires</div>
          <div className="rule-row">
            <select className="inp" value={rule.trigger.type} onChange={(e) => setTrigger(e.target.value as AutomationTriggerType)}>
              {TRIGGERS.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
            </select>
            {meta.param?.kind === "number" && (
              <input className="inp inp--num" type="number" value={Number(rule.trigger.params?.[meta.param.key] ?? 0)}
                onChange={(e) => setParam(meta.param!.key, Number(e.target.value))} />
            )}
            {meta.param?.kind === "band" && (
              <select className="inp" value={String(rule.trigger.params?.[meta.param.key] ?? "risk")} onChange={(e) => setParam(meta.param!.key, e.target.value)}>
                {(rule.trigger.type === "signal_created" ? ["risk", "watch", "positive"] : ["risk", "watch", "good"]).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
          </div>
        </section>

        {/* CONDITIONS */}
        <section className="rule-sec">
          <div className="rule-sec__hd"><span className="rule-sec__k">And</span> these conditions hold <span className="faint" style={{ fontWeight: 400 }}>(optional)</span></div>
          <div style={{ display: "grid", gap: 8 }}>
            {rule.conditions.map((c, i) => (
              <div key={i} className="rule-row">
                <select className="inp" value={c.field} onChange={(e) => setConditions(rule.conditions.map((x, j) => j === i ? { ...x, field: e.target.value } : x))}>
                  {FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <select className="inp inp--op" value={c.op} onChange={(e) => setConditions(rule.conditions.map((x, j) => j === i ? { ...x, op: e.target.value as ConditionOp } : x))}>
                  {OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                </select>
                <input className="inp" value={String(c.value)} onChange={(e) => setConditions(rule.conditions.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                <button className="mini-btn" title="Remove" onClick={() => setConditions(rule.conditions.filter((_, j) => j !== i))}><Icon name="dot" size={13} /></button>
              </div>
            ))}
            <button className="btn btn--sm btn--ghost" style={{ justifySelf: "start" }}
              onClick={() => setConditions([...rule.conditions, { field: "arr", op: "gte", value: 100000 }])}>
              <Icon name="plus" size={13} /> Add condition
            </button>
          </div>
        </section>

        {/* ACTION */}
        <section className="rule-sec">
          <div className="rule-sec__hd"><span className="rule-sec__k">Then</span> create &amp; assign a task</div>
          <div style={{ display: "grid", gap: 8 }}>
            <input className="inp" value={action.title} onChange={(e) => setAction({ title: e.target.value })} placeholder="Task title — use {account} / {signal}" />
            <div className="composer__grid">
              <Labeled label="Assign to">
                <select className="inp" value={encodeAssignee(action.assignee)} onChange={(e) => setAction({ assignee: parseAssignee(e.target.value) })}>
                  <optgroup label="Roles">{ws.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.label}</option>)}</optgroup>
                  <optgroup label="People">{ws.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
                </select>
              </Labeled>
              <Labeled label="Priority">
                <select className="inp" value={action.priority} onChange={(e) => setAction({ priority: e.target.value as TaskPriority })}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </Labeled>
              <Labeled label="Due in (days)">
                <input className="inp inp--num" type="number" value={action.dueInDays} onChange={(e) => setAction({ dueInDays: Number(e.target.value) })} />
              </Labeled>
              <Labeled label="Task type">
                <select className="inp" value={action.taskTypeId ?? ""} onChange={(e) => setAction({ taskTypeId: e.target.value || undefined })}>
                  <option value="">None</option>
                  {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Labeled>
            </div>
            {chosenType && chosenType.fields.some((f) => f.required) && (
              <div className="faint" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon name="lock" size={12} /> Created tasks can't be completed until: {chosenType.fields.filter((f) => f.required).map((f) => f.label).join(", ")}
              </div>
            )}
          </div>
        </section>

        {/* PREVIEW */}
        <section className="rule-sec">
          <div className="row between">
            <div className="rule-sec__hd" style={{ marginBottom: 0 }}><span className="rule-sec__k">Preview</span> {preview.length} task{preview.length === 1 ? "" : "s"} would be created now</div>
            <button className="btn btn--primary btn--sm" disabled={preview.length === 0} onClick={onRun}><Icon name="bolt" size={14} /> Run this rule</button>
          </div>
          <div className="preview-list">
            {preview.length === 0 ? (
              <div className="preview-empty">No accounts match right now — nothing would be created.</div>
            ) : preview.slice(0, 8).map((p, i) => (
              <div key={i} className="preview-row">
                <Icon name="check" size={13} className="faint" />
                <span className="preview-title">{p.input.title}</span>
                <span className="tag tag--role">{assigneeLabel(ws, p.input.assignee)}</span>
              </div>
            ))}
            {preview.length > 8 && <div className="faint" style={{ fontSize: 12, padding: "6px 10px" }}>+ {preview.length - 8} more</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="labeled"><span className="labeled__l">{label}</span>{children}</label>;
}
function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}>
    <i />{mode === "api" ? "Live API" : "Local demo"}
  </span>;
}
